import React, { useState } from 'react'
import { css } from '@emotion/core'
import { useAtSize } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { boolAttr } from '@fs/zion-frontend-friends'
import VisuallyHidden from '@reach/visually-hidden'
import { RTCInitialState } from '../../../state/RTCReducer'
import getEventTimeParts from '../../../helpers/dateTimeHelper'

const timerTextStyle = css`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  max-width: 400px;
  font-weight: 600;

  &[is-mobile] {
    justify-content: space-around;
  }
`
const digitContainerStyle = css`
  border-radius: 8px;
  background-color: var(--background-primary);
  width: fit-content;
  padding: 10px;
  color: #00a7e7;
  font-weight: 700;
  font-size: 48px;
`
const digitSetStyle = css`
  display: flex;
  & > div {
    margin-right: 2px;
    margin-left: 2px;
    line-height: 45px;
  }
`
const digitColumnStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  & + & {
    margin-left: 24px;
  }
`

const digitLabelStyle = css`
  margin-bottom: 10px;
  font-size: 14px;
  text-transform: uppercase;
`

const CountdownDigitGroup = ({ num }) => {
  const digitsArr = !num ? ['0', '0'] : num?.toString().split('')
  const digit1 = digitsArr.length === 2 ? digitsArr[0] : '0'
  const digit2 = digitsArr[digitsArr.length - 1]

  return (
    <div css={digitSetStyle}>
      <div css={digitContainerStyle}>{digit1}</div>
      <div css={digitContainerStyle}>{digit2}</div>
    </div>
  )
}

export default function CountDown({
  startTime,
  timesToShow = { days: true, hours: true, minutes: true, seconds: false },
}) {
  const initialValues = getEventTimeParts({ eventDateMS: startTime, offset: RTCInitialState.timeOffset })
  const [secondsRemaining, setSecondsRemaining] = useState(initialValues.seconds)
  const [minutesRemaining, setMinutesRemaning] = useState(initialValues.minutes)
  const [hoursRemaining, setHoursRemaining] = useState(initialValues.hours)
  const [daysRemaining, setDaysRemaining] = useState(initialValues.days)
  const [isCountdownComplete, setIsCountdownComplete] = useState(false)

  const [t] = useTranslation()

  React.useEffect(() => {
    const timer =
      !isCountdownComplete &&
      setInterval(() => {
        const timeDetails = getEventTimeParts({ eventDateMS: startTime, offset: RTCInitialState.timeOffset })
        setSecondsRemaining(timeDetails.seconds)
        setMinutesRemaning(timeDetails.minutes)
        setHoursRemaining(timeDetails.hours)
        setDaysRemaining(timeDetails.days)

        if (timeDetails.eventDateMS > 0 && timeDetails.eventDateMS - timeDetails.nowMS <= 0) {
          clearInterval(timer)
          setIsCountdownComplete(true)
          window.location.reload() // The live conference homepage will be presented upon reloading
        }
      }, 1000)
    return () => clearInterval(timer)
  }, [isCountdownComplete, startTime])
  const atSize = useAtSize()
  const isMobile = atSize({ default: true, sm: false })
  return (
    <>
      {/* there was not a reasonable way to make the countdown accessible. So we have a separate feature for screen readers. */}
      <VisuallyHidden>
        {!timesToShow.days ? null : (
          <>
            {daysRemaining} {t('homepage.countdown.days', 'Days')}{' '}
          </>
        )}
        {!timesToShow.hours ? null : (
          <>
            {hoursRemaining} {t('homepage.countdown.hours', 'Hours')}{' '}
          </>
        )}
        {!timesToShow.minutes ? null : (
          <>
            {minutesRemaining} {t('homepage.countdown.minutes', 'Minutes')}{' '}
          </>
        )}
        {!timesToShow.seconds ? null : (
          <>
            {secondsRemaining} {t('homepage.countdown.secondsRemaining', 'Seconds')}{' '}
          </>
        )}
      </VisuallyHidden>

      <div css={timerTextStyle} is-mobile={boolAttr(isMobile)} aria-hidden>
        {!timesToShow.days ? null : (
          <div css={digitColumnStyle}>
            <div css={digitLabelStyle}>{t('homepage.countdown.days', 'Days')}</div>
            <CountdownDigitGroup num={daysRemaining} />
          </div>
        )}
        {!timesToShow.hours ? null : (
          <div css={digitColumnStyle}>
            <div css={digitLabelStyle}>{t('homepage.countdown.hours', 'Hours')}</div>
            <CountdownDigitGroup num={hoursRemaining} />
          </div>
        )}
        {!timesToShow.minutes ? null : (
          <div css={digitColumnStyle}>
            <div css={digitLabelStyle}>{t('homepage.countdown.minutes', 'Minutes')}</div>
            <CountdownDigitGroup num={minutesRemaining} />
          </div>
        )}
        {!timesToShow.seconds ? null : (
          <div css={digitColumnStyle}>
            <div css={digitLabelStyle}>{t('homepage.countdown.secondsRemaining', 'Seconds')}</div>
            <CountdownDigitGroup num={secondsRemaining} />
          </div>
        )}
      </div>
    </>
  )
}
